// import { LoginCallback } from '@okta/okta-vue';
import LoginCallback from '@/components/LoginCallback';
import LoggedOut from '@/components/LoggedOut';
import Login from '@/components/Login';
import Intermediate from '@/components/Intermediate';
import Search from '@/components/Search';
import Home from '@/components/Home';
import Admin from '@/components/admin/Admin';
import Settings from '@/components/admin/Settings';
import AddAgency from '@/components/admin/agencies/AddAgency';
import EditAgency from '@/components/admin/agencies/EditAgency';
import AgencyManager from '@/components/admin/agencies/AgencyManager';
import Carriers from '@/components/admin/carriers/CarrierManager';
import EditCarrier from '@/components/admin/carriers/EditCarrier';
import AddCarrier from '@/components/admin/carriers/AddCarrier';
import AddUser from '@/components/admin/user/AddUser';
import ImportUsers from '@/components/admin/user/ImportUsers';
import EditUser from '@/components/admin/user/EditUser';
import TrainingVideo from '@/components/admin/TrainingVideo';
import VideoDetail from '@/components/admin/VideoDetail';
import UserManager from '@/components/admin/user/UserManager';
import PReports from '@/components/p_admin/Reports';
import SectionDisplay from '@/components/interview/propertyInsurance/SectionDisplay';
import CarrierMatches from '@/components/interview/propertyInsurance/CarrierMatches';
import propCheckout from '@/components/interview/propertyInsurance/propCheckout';
import propConfirmation from '@/components/interview/propertyInsurance/propConfirmation';
import SectionDisplayRcTwo from '@/components/interview/propertyInsurance/SectionDisplayRcTwo';
import Interview from '@/components/interview/Interview';
import ResetPassword from '@/components/user/ResetPassword';
import CrmImport from '@/components/interview/CrmImport';
import epicimport from '@/components/interview/EpicImport';
import notFound from '@/components/interview/NotFound';
import NonEndToEnd from '@/components/interview/shared/NonEndToEnd';

import variations from '../../config/variations';
import common from '../services/common';
import store from '../store/store';


/**
 * Polyfill for `searchParams` since IE 11 does not support it.
 * https://developer.mozilla.org/en-US/docs/Web/API/URL/searchParams
 */
const qs = ((a) => {
  if (a === '') return {};
  const b = {};

  for (let i = 0; i < a.length; i += 1) {
    const p = a[i].split('=', 2);

    if (p.length === 1) {
      b[p[0]] = '';
    } else {
      b[p[0]] = decodeURIComponent(p[1].replace(/\+/g, ' '));
    }
  }

  return b;
})(window.location.search.substr(1).split('&'));

let variationValue = qs.v;
const sessionVariationValue = sessionStorage.getItem('variation');

if (!variationValue && !sessionVariationValue) {
  variationValue = '1.1'; // Not set, not stored, so use default of '1.0'
} else if (!variationValue && sessionVariationValue) {
  variationValue = sessionVariationValue; // Not set, yes stored, so use stored value
}

// Ensure the variation value is the name is a known index
switch (variationValue) {
  case '1.0':
    variationValue = '1.0';
    break;

  case '1.1':
    variationValue = '1.1';
    break;

  default:
    variationValue = '1.1'; // Default design
    break;
}

sessionStorage.setItem('variation', variationValue); // Set the value in storage

const routes = [
  {
    path: '/notFound',
    component: notFound,
    name: '404',
  },
  { path: '/intermediate',
    component: Intermediate,
    name: 'intermediate',
    meta: { requiresAuth: false, requiresSession: false }, // Infinite loop if auth required for log in :)
    beforeEnter(to, from, next) {
      store.dispatch('setAgencyLogo');
      next();
    },
  },
  { path: '/user/reset-password/:viewUsage?',
    component: ResetPassword,
    props: route => ({
      guid: route.query.guid, // User's GUID
      token: encodeURIComponent(route.query.token), // Token to check that the user is authenticated
    }),
    name: 'resetPassword',
    meta: { requiresAuth: false, requiresSession: false },
    beforeEnter(to, from, next) {
      store.dispatch('setAgencyLogo');
      next();
    },
  },
  { path: '/dashboard',
    component: Admin,
    name: 'admin/dashboard',
    meta: {
      requiresAuth: true,
      requiresSession: false,
      title: 'Dashboard',
    },
    beforeEnter(to, from, next) {
      store.dispatch('setAgencyLogo');
      next();
    },
    children: [
      {
        path: '_settings',
        meta: {
          title: 'Settings',
        },
        name: 'settings',
        component: Settings,
      },
      {
        path: 'agency-details', // Separate from 'users' to link the breadcrumbs properly
        name: 'agency-details',
        component: EditAgency,
        meta: {
          title: 'Agency Details',
        },
        beforeEnter(to, from, next) {
          if (common.checkPermission('UpdateAgency')) {
            next();
          } else {
            next({ // Route requires NOT auth and is logged in, so go to search route.
              path: '/dashboard',
            });
          }
        },
      },
      {
        path: 'agencies/agencymanager', // Separate from 'users' to link the breadcrumbs properly
        name: 'agencymanager',
        meta: {
          title: 'Agency Manager',
        },
        component: AgencyManager,
        beforeEnter(to, from, next) {
          if (common.checkPermission('GetAllAgencies')) {
            next();
          } else {
            next({ // Route requires NOT auth and is logged in, so go to search route.
              path: '/dashboard',
            });
          }
        },
      },
      {
        path: 'agencies',
        name: 'agencies',
        meta: {
          title: '',
        },
        component: {
          render(c) { return c('router-view'); },
        },
        children: [
          {
            path: 'addagency',
            name: 'addagency',
            meta: {
              title: 'Add Agency',
            },
            component: AddAgency,
            beforeEnter(to, from, next) {
              if (common.checkPermission('CreateAgency')) {
                next();
              } else {
                next({ // Route requires NOT auth and is logged in, so go to search route.
                  path: '/dashboard',
                });
              }
            },
          },
          {
            path: 'agencymanager',
            // name: 'manage-users', // No name since this child will always lead to one of its children
            meta: {
              title: 'Agency Manager',
            },
            component: {
              render(c) { return c('router-view'); },
            },
            children: [
              {
                path: 'editagency',
                name: 'editagency',
                component: EditAgency,
                meta: {
                  title: 'Edit Agency',
                },
                beforeEnter(to, from, next) {
                  if (common.checkPermission('UpdateAgency')) {
                    next();
                  } else {
                    next({ // Route requires NOT auth and is logged in, so go to search route.
                      path: '/dashboard',
                    });
                  }
                },
              },
            ],
          },
          {
            path: 'agencymanager',
            // name: 'agencymanager',
            meta: {
              title: 'Agency Manager',
            },
            component: AgencyManager,
            beforeEnter(to, from, next) {
              if (common.checkPermission('GetAllAgencies')) {
                next();
              } else {
                next({ // Route requires NOT auth and is logged in, so go to search route.
                  path: '/dashboard',
                });
              }
            },
          },
        ],
      },
      {
        path: 'training/videos', // Separate from 'users' to link the breadcrumbs properly
        name: 'training/videos',
        component: TrainingVideo,
        meta: {
          title: 'Training Videos',
        },
      },
      {
        path: 'training',
        name: 'training',
        meta: {
          title: '',
        },
        component: {
          render(c) { return c('router-view'); },
        },
        children: [
          {
            path: 'videos',
            // name: 'manage-users', // No name since this child will always lead to one of its children
            meta: {
              title: 'Training Videos',
            },
            component: {
              render(c) { return c('router-view'); },
            },
            children: [
              {
                path: 'video-detail',
                name: 'video-detail',
                component: VideoDetail,
                meta: {
                  title: 'Video Detail',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'users/manage-users', // Separate from 'users' to link the breadcrumbs properly
        name: 'manage-users',
        component: UserManager,
        meta: {
          title: 'Manage Users',
        },
        beforeEnter(to, from, next) {
          if (common.checkPermission('GetAgencyUsers') || common.checkPermission('GetAllUsers')) {
            next();
          } else {
            next({ // Route requires NOT auth and is logged in, so go to search route.
              path: '/dashboard',
            });
          }
        },
      },
      {
        path: 'users',
        name: 'users',
        meta: {
          title: '',
        },
        component: {
          render(c) { return c('router-view'); },
        },
        children: [
          {
            path: 'manage-users',
            // name: 'manage-users', // No name since this child will always lead to one of its children
            meta: {
              title: 'Manage Users',
            },
            component: {
              render(c) { return c('router-view'); },
            },
            children: [
              {
                path: 'edit-user',
                name: 'edit-user',
                component: EditUser,
                meta: {
                  title: 'Edit User',
                },
              },
            ],
          },
          {
            path: 'import-users',
            meta: {
              title: 'Import Users',
            },
            name: 'users/import-users',
            component: ImportUsers,
            beforeEnter(to, from, next) {
              if (common.checkPermission('CreateUser')) {
                next();
              } else {
                next({ // Route requires NOT auth and is logged in, so go to search route.
                  path: '/dashboard',
                });
              }
            },
          },
          {
            path: 'adduser',
            name: 'adduser',
            component: AddUser,
            meta: {
              title: 'Add User',
            },
            beforeEnter(to, from, next) {
              if (common.checkPermission('CreateUser')) {
                next();
              } else {
                next({ // Route requires NOT auth and is logged in, so go to search route.
                  path: '/dashboard',
                });
              }
            },
          },
        ],
      },
      {
        path: 'your-profile', // Separate from 'users' to link the breadcrumbs properly
        name: 'your-profile',
        component: EditUser,
        meta: {
          title: 'Your Profile',
        },
      },
      {
        path: 'carriers/carrier-manager', // Separate from 'users' to link the breadcrumbs properly
        name: 'carrier-manager',
        component: Carriers,
        meta: {
          title: 'Carrier Manager',
        },
      },
      {
        path: 'carriers',
        name: 'carriers',
        meta: {
          title: '',
        },
        component: {
          render(c) { return c('router-view'); },
        },
        children: [
          {
            path: 'carrier-manager',
            // name: 'manage-users', // No name since this child will always lead to one of its children
            meta: {
              title: 'Carrier Manager',
            },
            component: {
              render(c) { return c('router-view'); },
            },
            children: [
              {
                path: 'edit-carrier',
                name: 'edit-carrier',
                component: EditCarrier,
                meta: {
                  title: 'Edit Carrier',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'carriers/add-carrier',
        meta: {
          title: '+Add Carrier',
        },
        name: 'carriers/add-carrier',
        component: AddCarrier,
      },
      {
        path: 'reports',
        meta: {
          title: '',
        },
        name: 'admin/reports',
        component: PReports,
        children: [
          {
            path: 'carriers',
            meta: {
              title: 'Carrier Report',
            },
            name: 'reports/carriers',
          },
          {
            path: 'teams',
            meta: {
              title: 'Team Report',
            },
            name: 'reports/teams',
          },
          {
            path: 'states',
            meta: {
              title: 'State Report',
            },
            name: 'reports/states',
          },
          {
            path: 'agents',
            meta: {
              title: 'Agent Report',
            },
            name: 'reports/agents',
          },
          {
            path: 'products',
            meta: {
              title: 'Product Report',
            },
            name: 'reports/products',
          },
        ],
      },
    ],
  },
  { path: '/property-insurance',
    component: Interview,
    name: 'property-insurance',
    meta: {
      requiresAuth: true,
      requiresSession: true,
      title: 'Property Insurance Interview',
    },
    beforeEnter(to, from, next) {
      store.dispatch('setAgencyLogo');
      next();
    },
    children: [
      {
        path: 'homeowner',
        component: SectionDisplay,
        name: 'property-insurance/homeowner',
        meta: {
          title: 'Homeowner Insurance',
        },
      },
      {
        path: 'condo',
        component: SectionDisplay,
        name: 'property-insurance/condo',
        meta: {
          title: 'Condo Insurance',
        },
      },
      {
        path: 'renter',
        component: SectionDisplay,
        name: 'property-insurance/renter',
        meta: {
          title: 'Renter Insurance',
        },
      },
    ],
  },
  { path: '/search',
    component: Search,
    props: route => ({
      qId: route.query.id, // User's GUID
      loc: route.query.loc, // Interview resource
    }),
    name: 'search',
    meta: { requiresAuth: true, requiresSession: false },
    beforeEnter(to, from, next) {
      store.dispatch('setAgencyLogo');
      next();
    },
  },
  { path: '/crmImport',
    component: CrmImport,
    props: route => ({
      callbackurl: route.query.callbackurl, // User's GUID
      quoteId: route.query.quoteId,
      action: route.query.action,
      quotebundle: route.query.quotebundle,
      quadClickKey: route.query.CLK,
    }),
    name: 'crmimport',
    meta: { requiresAuth: true, requiresSession: false },
    beforeEnter(to, from, next) {
      store.dispatch('setAgencyLogo');
      next();
    },
  },
  { path: '/epicimport',
    component: epicimport,
    props: route => ({
      Area: route.query.area,
      User: route.query.user,
      LineID: route.query.LineID,
      EntityID: route.query.EntityID,
      A: route.query.a,
      sessionid: route.query.sessionid,
      // sessionid is for demo purpose ,it should remove for real product.
    }),
    name: 'epicimport',
    meta: { requiresAuth: true, requiresSession: false },
    beforeEnter(to, from, next) {
      store.dispatch('setAgencyLogo');
      next();
    },
  },
  { path: '/interview',
    component: Interview,
    name: 'interview',
    meta: { requiresAuth: true, requiresSession: true },
    beforeEnter(to, from, next) {
      store.dispatch('setAgencyLogo');
      next();
    },
    children: [
      {
        path: '/auto',
        name: 'interview/auto',
        component: {
          render(c) { return c('router-view'); },
        },
        meta: {
          productType: 'auto',
        },
        children: variations[variationValue],
      },
      {
        path: '/home',
        name: 'interview/home',
        component: {
          render(c) { return c('router-view'); },
        },
        meta: {
          productType: 'home',
        },
        children: [ // TODO: Use config values
          {
            path: 'name-insured',
            name: 'home/name-insured',
            component: SectionDisplay,
            meta: { parentPath: 'rc1', page: 'property-details' },
          },
          {
            path: 'coverages',
            name: 'home/coverages',
            component: SectionDisplay,
            meta: { parentPath: 'rc1', page: 'coverages' },
          },
          {
            path: 'occupants',
            name: 'home/occupants',
            component: SectionDisplay,
            meta: { parentPath: 'rc1', page: 'occupants' },
          },
          {
            path: 'prior-insurance',
            name: 'home/prior-insurance',
            component: SectionDisplay,
            meta: { parentPath: 'rc1', page: 'name-insured' },
          },
          {
            path: 'property-details',
            name: 'home/property-details',
            component: SectionDisplay,
            meta: { parentPath: 'rc1', page: 'property-details' },
          },
          {
            path: 'structure-details',
            name: 'home/structure-details',
            component: SectionDisplay,
            meta: { parentPath: 'rc1', page: 'structure-details' },
          },
          {
            path: 'carrier-matches',
            name: 'home/carrier-matches',
            component: CarrierMatches,
            meta: { parentPath: 'rc1', page: 'carrier-matches' },
          },
          {
            path: '**/rc2-policy-details',
            name: 'home/rc2-policy-details',
            component: SectionDisplay,

          },
          {
            path: 'dwelling',
            name: 'home/dwelling',
            component: SectionDisplayRcTwo,
            meta: { parentPath: 'rc2', page: 'hodwelling' },
          },
          {
            path: 'mortgage',
            name: 'home/mortgage',
            component: SectionDisplayRcTwo,
            meta: { parentPath: 'rc2', page: 'homortgage' },
          },
          {
            path: 'coverage',
            name: 'home/coverage',
            component: SectionDisplayRcTwo,
            meta: { parentPath: 'rc2', page: 'hocoverage' },
          },
          {
            path: 'hopolicydetails',
            name: 'home/policydetails',
            component: SectionDisplayRcTwo,
            meta: { parentPath: 'rc2', page: 'hopolicydetails' },
          },
          {
            path: 'checkout',
            name: 'home/checkout',
            component: propCheckout,
            meta: { parentPath: 'rc2', page: 'hocheckout' },
          },
          {
            path: 'confirmation',
            name: 'home/confirmation',
            component: propConfirmation,
            meta: { parentPath: 'rc2', page: 'hoconfirmation' },
          },
          {
            path: 'non-end-to-end',
            name: 'home/non-end-to-end',
            component: NonEndToEnd,
            meta: { parentPath: 'rc2', page: 'hononendtoend' },
          },
        ],
      },
      {
        path: '/condo',
        component: {
          render(c) { return c('router-view'); },
        },
        name: 'interview/condo',
        meta: {
          productType: 'condo',
        },
        children: [ // TODO: Use config values
          {
            path: 'name-insured',
            name: 'condo/name-insured',
            component: SectionDisplay,
          },
          {
            path: 'coverages',
            name: 'condo/coverages',
            component: SectionDisplay,
          },
          {
            path: 'occupants',
            name: 'condo/occupants',
            component: SectionDisplay,
          },
          {
            path: 'prior-insurance',
            name: 'condo/prior-insurance',
            component: SectionDisplay,
          },
          {
            path: 'property-details',
            name: 'condo/property-details',
            component: SectionDisplay,
          },
          {
            path: 'structure-details',
            name: 'condo/structure-details',
            component: SectionDisplay,
          },
          {
            path: 'carrier-matches',
            name: 'condo/carrier-matches',
            component: CarrierMatches,
          },
          {
            path: 'non-end-to-end',
            name: 'condo/non-end-to-end',
            component: NonEndToEnd,
            meta: { parentPath: 'rc2', page: 'cononendtoend' },
          },
        ],
      },
      {
        path: '/renters',
        component: {
          render(c) { return c('router-view'); },
        },
        name: 'interview/renters',
        meta: {
          productType: 'renters',
        },
        children: [ // TODO: Use config values
          {
            path: 'name-insured',
            name: 'renters/name-insured',
            component: SectionDisplay,
          },
          {
            path: 'coverages',
            name: 'renters/coverages',
            component: SectionDisplay,
          },
          {
            path: 'occupants',
            name: 'renters/occupants',
            component: SectionDisplay,
          },
          {
            path: 'prior-insurance',
            name: 'renters/prior-insurance',
            component: SectionDisplay,
          },
          {
            path: 'property-details',
            name: 'renters/property-details',
            component: SectionDisplay,
          },
          {
            path: 'structure-details',
            name: 'renters/structure-details',
            component: SectionDisplay,
          },
          {
            path: 'carrier-matches',
            name: 'renters/carrier-matches',
            component: CarrierMatches,
          },
          {
            path: 'non-end-to-end',
            name: 'renters/non-end-to-end',
            component: NonEndToEnd,
            meta: { parentPath: 'rc2', page: 'renonendtoend' },
          },
        ],
      },
    ],
  },
];

if (process.env.IS_OKTA_LOGIN_ENABLED) {
  routes.push({
    path: '/login/callback',
    component: LoginCallback,
  });
  routes.push({
    path: '/',
    component: Home,
    name: 'home',
  });
  routes.push({
    path: '/LoggedOut',
    component: LoggedOut,
    name: 'LoggedOut',
  });
} else {
  routes.push({
    path: '/',
    component: Login,
    name: 'login',
    meta: { requiresAuth: false, requiresSession: false }, // Infinite loop if auth required for log in :)
    beforeEnter(to, from, next) {
      store.dispatch('setAgencyLogo');
      const tokenId = sessionStorage.getItem('accessToken'); // TODO: Detect authenticated session
      if (tokenId) {
        next({ // Route requires NOT auth and is logged in, so go to search route.
          path: '/search',
          query: { redirect: to.fullPath },
        });
      } else {
        next(); // Route requires NOT auth and is not logged in, so continue.
      }
      next();
    },
  });
}

export default routes;



// WEBPACK FOOTER //
// ./src/router/routes.js